import { action, makeObservable, observable } from 'mobx';
import { ReactionManager } from './reaction-manager';

export type DataFrameOptions = {
  autoFetch?: 'off' | 'on' | 'nextTick';
};

export abstract class DataFrame<T, FetchT = any> extends ReactionManager {
  private lastUpdateRequest: number = 0;

  @observable
  fetching: boolean = false;
  @observable
  data: T | null = null;
  @observable.ref
  error: Error | null = null;

  keepPreviousDataWhiteFetching = true;
  keepDataOnFetchError = false;

  private promise: Promise<void> | null = null;
  private existingAbortController: AbortController | null = null;

  constructor(options: DataFrameOptions = {}) {
    super();
    makeObservable(this);

    if (options.autoFetch === 'on') {
      this.get().catch(action((e) => (this.error = e)));
    } else if (options.autoFetch === 'nextTick') {
      setTimeout(() => this.get().catch(action((e) => (this.error = e))), 1);
    }
  }

  protected abstract fetch(abortSignal: AbortSignal): Promise<FetchT>;

  protected postProcess(fetchedData: FetchT): T {
    return fetchedData as unknown as T;
  }

  @action
  private setData(data: DataFrame<T>['data']) {
    this.data = data;
  }

  @action
  private setError(error: DataFrame<T>['error']) {
    this.error = error;
  }

  @action
  private setFetching(fetching: DataFrame<T>['fetching']) {
    this.fetching = fetching;
  }

  private internalFetch = async (abortSignal?: AbortSignal) => {
    this.setFetching(true);

    if (this.existingAbortController) {
      this.existingAbortController.abort();
      this.existingAbortController = null;
    }

    const myFetchId = ++this.lastUpdateRequest;

    const myAbortSignal = abortSignal
      ? abortSignal
      : (this.existingAbortController = new AbortController()).signal;

    try {
      if (!this.keepPreviousDataWhiteFetching) {
        this.setData(null);
      }
      this.setError(null);
      const rawData = await this.fetch(myAbortSignal);

      if (this.lastUpdateRequest !== myFetchId) {
        return console.warn(
          `Preventing DataFrame ${this.constructor.name} from manifesting outdated results`,
        );
      }

      this.setData(this.postProcess(rawData));

      this.setFetching(false);
      this.promise = null;
      this.existingAbortController = null;
    } catch (e) {
      if (myAbortSignal?.aborted) {
        // we imediately abort, and we don't touch the state of the dataframe
        return console.warn(`DataFrame ${this.constructor.name} aborted`);
      }

      this.setError(e as Error);
      if (!this.keepDataOnFetchError) {
        this.setData(null);
      }

      this.setFetching(false);
      this.promise = null;
      this.existingAbortController = null;
    }
  };

  async get(update: boolean = false, abortSignal?: AbortSignal): Promise<T> {
    if (update || (!this.data && !this.fetching)) {
      this.promise = this.internalFetch(abortSignal);
    }

    await this.promise;

    if (this.error) {
      throw this.error;
    }

    return this.data!;
  }

  read() {
    if (this.promise) {
      throw this.promise;
    }
    if (this.error) {
      throw this.error;
    }
    return this.data!;
  }

  async populate(): Promise<this> {
    await this.get();
    return this;
  }

  update = (abortSignal?: AbortSignal) => this.get(true, abortSignal);

  abort() {
    if (this.existingAbortController) {
      this.existingAbortController.abort();
    }
  }
}
